/* ═══════════════════════════════════════════════════════
   ClientCardView — the Session Sheet, rendered from a CLIENT card snapshot.
   ═══════════════════════════════════════════════════════
   What the client (and anyone they forward it to) sees: where to go, who to
   reach, what is being captured, what to bring, what happens, afterwards.
   Never money, never the crew list. Same public page as the crew card; the
   snapshot's `kind` picks this view. Page and phone layouts (container
   width), Normcore and Cool (snap.style), screen and print.

   Compatibility is by property presence, never by `v`: a sheet deployed
   before the second contact existed carries `offSite`; one from before
   slice 2 has no `capturing`, `afterwards`, `lengthMinutes` or `style`.
   Every one of those still renders; empty sections vanish. */
import React, { useState } from "react";
import {
  Sheet, Letterhead, Section, Cols, Tiles, FactList, Foot,
  fmtTime, fmtDate, splitLines, asList, telHref, smsHref,
} from "./sheet.jsx";

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^\d{2}:\d{2}$/;
const nextDay = (iso) => { const [y, m, d] = iso.split("-").map(Number); return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10); };
const isHttps = (u) => /^https:\/\//i.test(String(u || ""));
const hhmm = (mins) => `${String(Math.floor(mins / 60) % 24).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;

/* One .ics event in New York time. The end is call + lengthMinutes when the
   sheet carries a positive whole number of minutes, else the wrap time, else
   call + 2 h. An end at or before the call time is after midnight, so the
   event ends the next day. */
export function buildIcs(snap, url) {
  if (!DATE_RE.test(snap?.date || "") || !TIME_RE.test(snap?.callTime || "")) return "";
  const stamp = (d, t) => d.replace(/-/g, "") + "T" + t.replace(":", "") + "00";
  const [ch, cm] = snap.callTime.split(":").map(Number);
  // 1..1440 as the twins clamp it; the view does not trust a hand-made snapshot.
  const length = Number.isInteger(snap.lengthMinutes) && snap.lengthMinutes > 0 && snap.lengthMinutes <= 1440 ? snap.lengthMinutes : 0;
  let end, endDate;
  if (length) {
    const total = ch * 60 + cm + length;
    end = hhmm(total);
    endDate = total >= 24 * 60 ? nextDay(snap.date) : snap.date;
  } else {
    end = TIME_RE.test(snap.wrapTime || "") ? snap.wrapTime : hhmm(ch * 60 + cm + 120);
    endDate = end > snap.callTime ? snap.date : nextDay(snap.date);
  }
  const esc = (s) => String(s || "").replace(/\r/g, "").replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/,/g, "\\,").replace(/;/g, "\\;");
  const desc = [snap.arrival, snap.onSite?.name ? `On-site contact: ${snap.onSite.name} ${snap.onSite.phone || ""}`.trim() : "", url ? `Session sheet: ${url}` : ""].filter(Boolean).join("\\n");
  return [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//New York Capture//Session Sheet//EN", "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:${(snap.sessionId || "session")}-${snap.date}@nycsuite.io`,
    `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, "").slice(0, 15)}Z`,
    `DTSTART;TZID=America/New_York:${stamp(snap.date, snap.callTime)}`,
    `DTEND;TZID=America/New_York:${stamp(endDate, end)}`,
    "SUMMARY:New York Capture session",
    `LOCATION:${esc(snap.location?.line)}`,
    `DESCRIPTION:${esc(desc)}`,
    url ? `URL:${url}` : "",
    "END:VEVENT", "END:VCALENDAR",
  ].filter(Boolean).join("\r\n");
}

/* The sheet reads differently depending on who is travelling and what is in
   front of the camera. On location we are the ones arriving, so "Arrive"
   and "Where to go" are addressed to the wrong person; and "For the talent"
   means nothing on a job with no person in it. snap.subject is "people",
   "objects", "places" or "" (unknown, the generic labels). */
export function labelsFor(snap) {
  const onLocation = !(snap.location || {}).isStudio;
  const subject = snap.subject || "";
  /* The deliverable text is typed by hand and often already states the
     turnaround ("Raw models delivered within 3-5 business days from processing
     approval"), so the separate turnaround line said it twice. */
  const d = snap.deliverable || {};
  const turn = String(d.turnaround || "");
  const format = String(d.format || "");
  const nums = turn.match(/\d+/g) || [];
  // Whole numbers only ("1" must not match "10"), and the turnaround's own
  // unit family must appear, so "3–5 weeks" never hides "3 to 5 business days".
  const unit = /week/i.test(turn) ? /\bweeks?\b/i : /day/i.test(turn) ? /\bdays?\b/i : null;
  const repeatsTurnaround = nums.length > 0 && !!unit && unit.test(format)
    && nums.every((n) => new RegExp(`(^|\\D)${n}(?!\\d)`).test(format));
  return {
    onLocation,
    repeatsTurnaround,
    arrive: onLocation ? "We arrive" : "Arrive",
    where: onLocation ? "Where we're coming" : "Where to go",
    bring: onLocation ? "What we need on site" : subject === "people" ? "For the talent" : "What to bring",
    forwardable: subject === "people" || !subject,
  };
}

/* "about 45 minutes" -> "About 45 min"; "about 1.5 hours" -> "About 1.5 hours". */
const lengthTile = (duration) => {
  const s = String(duration || "").trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/\bminutes\b/, "min") : "";
};

/* One "Who to reach" row: name and where they are, the number, Call / Text
   (the buttons drop out of print; the number stays). */
function Contact({ name, phone, role }) {
  return (
    <div className="who">
      <span><b>{name}</b><span className="r">{role}</span></span>
      {phone ? <span className="num">{phone}</span> : <span />}
      {phone ? <span className="acts"><a className="mini" href={telHref(phone)}>Call</a><a className="mini" href={smsHref(phone)}>Text</a></span> : <span />}
    </div>
  );
}

export default function ClientCardView({ snap, token = "", version = 0, preview = false }) {
  const [shared, setShared] = useState("");
  if (!snap) return null;
  const loc = snap.location || {};
  const L = labelsFor(snap);
  /* The second contact. Snapshots deployed before it existed carry offSite
     (always Ivin, always off site) and no `second`; their links are live in
     client inboxes, so they still render. Selected by property presence, not
     truthiness (the stored-shape rule): a snapshot that carries `second`
     shows exactly that, `null` included, and `offSite` is read only when
     `second` is absent. */
  const second = Object.prototype.hasOwnProperty.call(snap, "second")
    ? snap.second
    : (snap.offSite?.name ? { ...snap.offSite, onSite: false } : null);
  const cleanUrl = token ? `${typeof window !== "undefined" ? window.location.origin : "https://nycsuite.io"}/card/${token}` : "";
  const ics = buildIcs(snap, cleanUrl);
  const icsHref = ics ? "data:text/calendar;charset=utf-8," + encodeURIComponent(ics) : "";
  const d = snap.deliverable || {};
  const afterwards = asList(snap.afterwards).map((x) => String(x || "").trim()).filter(Boolean);
  const legacyAfter = [d.format, L.repeatsTurnaround ? "" : d.turnaround ? `Turnaround ${d.turnaround}.` : ""].filter(Boolean).join(" ");
  const arrival = splitLines(snap.arrival);
  const capturing = splitLines(snap.capturing);
  const bring = splitLines(snap.bring);
  const expect = splitLines(snap.expect);
  const share = async () => {
    if (!cleanUrl) return;
    try {
      if (navigator.share) { await navigator.share({ title: "New York Capture session", url: cleanUrl }); setShared("Shared."); return; }
      await navigator.clipboard.writeText(cleanUrl); setShared("Link copied. Paste it into a text or email.");
    } catch { setShared(cleanUrl); }
  };

  return (
    <Sheet snap={snap} kind="client">
      <Letterhead doc="Session sheet" line={version ? `Sheet v${version}` : ""} />
      <h1 className="date">{fmtDate(snap.date) || "Date to be confirmed"}</h1>
      <Tiles tiles={[
        fmtTime(snap.callTime)
          ? { label: L.arrive, value: fmtTime(snap.callTime), hot: true }
          : { label: L.arrive, value: "Time to be confirmed", hot: true, text: true },
        { label: "Length", value: lengthTile(snap.duration) },
      ]} />

      <Section title={L.where}>
        <div className="addr">{loc.line || "Location to be confirmed"}</div>
        {loc.line && (
          <div className="btns">
            {isHttps(loc.mapsGoogle) && <a className="btn" href={loc.mapsGoogle} target="_blank" rel="noreferrer">Google Maps</a>}
            {isHttps(loc.mapsApple) && <a className="btn" href={loc.mapsApple} target="_blank" rel="noreferrer">Apple Maps</a>}
            {icsHref && !preview && <a className="btn pri" href={icsHref} download="new-york-capture-session.ics">Add to calendar</a>}
          </div>
        )}
        <FactList lines={arrival} />
      </Section>

      {(snap.onSite?.name || second?.name) && (
        <Section title="Who to reach">
          {snap.onSite?.name && <Contact name={snap.onSite.name} phone={snap.onSite.phone} role="On site, day of" />}
          {second?.name && <Contact name={second.name} phone={second.phone} role={second.onSite ? "On site, New York Capture" : "Off site, New York Capture"} />}
        </Section>
      )}

      <Cols>
        {capturing.length > 0 && <Section title="What we're capturing"><FactList lines={capturing} /></Section>}
        {bring.length > 0 && <Section title={L.bring}><FactList lines={bring} /></Section>}
      </Cols>

      <Cols>
        {expect.length > 0 && <Section title="What happens"><FactList lines={expect} /></Section>}
        {(afterwards.length > 0 || legacyAfter) && (
          <Section title="Afterwards"><FactList lines={afterwards.length ? afterwards : [legacyAfter]} /></Section>
        )}
      </Cols>

      {!preview && cleanUrl && (
        <div className="share">
          {L.forwardable ? "Forwarding this to the person being scanned? Share the sheet itself, not this page's address." : "Passing this to someone else on your side? Share the sheet itself, not this page's address."}
          <div><button type="button" className="btn" onClick={share}>{L.forwardable ? "Share with the person being scanned" : "Share this sheet"}</button></div>
          {shared && <div className="said">{shared}</div>}
        </div>
      )}

      <Foot
        left={`Questions: reply to the email this came with${snap.studio?.email ? `, or write to ${snap.studio.email}` : ""}.`}
        right="New York Capture"
      />
    </Sheet>
  );
}
